/**
 * Site-wide floating table of contents ("On this page").
 *
 * Progressive enhancement over snippets/table-of-contents.liquid: scans the
 * page's <main> for headings at the configured levels, assigns slugified ids
 * to headings that lack one, and drives the floating button + expandable nav.
 * Configuration arrives as data attributes set from Theme settings.
 */
(() => {
  if (customElements.get('table-of-contents')) return;

  class TableOfContents extends HTMLElement {
    connectedCallback() {
      // Relocating the element re-fires connectedCallback — init only once.
      if (this.initialized) return;
      this.initialized = true;

      this.main = document.getElementById('MainContent') || document.querySelector('main');
      if (!this.main) return;

      // Stand down where a section already provides an on-page nav
      // (sections/tcl-page-nav.liquid, the §2 treatment-page sticky nav). Two
      // simultaneous "on this page" navigations is a bug, and the curated one
      // wins: its links are authored, ordered and labelled by a merchant,
      // whereas this widget infers its list by scraping headings.
      //
      // Resolved here rather than by adding each treatment page's handle to
      // settings.toc_exclude_handles, because that means editing
      // config/settings_data.json — the one file co-owned with the Theme Editor
      // and the one that can carry pasted secrets — and repeating it by hand
      // for every treatment page as the other nine land in Phase 6.
      //
      // `[data-tcl-inpage-nav]` is the general form of the same marker, added
      // for sections/tcl-price-list.liquid, whose sticky filter bar IS this
      // page's on-page navigation — it replaced the incumbent's 13 hash links.
      //
      // Standing down there is not only about duplicate navigations. This widget
      // WRITES `heading.style.scrollMarginTop` inline (see applyScrollMargins),
      // at an offset sized for the header alone, on every heading it scrapes.
      // On a page with its own taller sticky bar that silently clobbers the
      // section's anchor offset — inline, so no author rule of any specificity
      // can win it back, and invisible to any search of the CSS. It cost a long
      // debug on the price list: two rules were blamed and neither was guilty.
      // If a future section needs its own anchor offset, mark it here.
      if (document.querySelector('[data-tcl-pagenav], [data-tcl-inpage-nav]')) {
        this.remove();
        return;
      }

      this.levels = (this.dataset.levels || 'h2,h3').split(',').filter(Boolean);
      this.minHeadings = parseInt(this.dataset.minHeadings, 10) || 2;
      this.extraOffset = parseInt(this.dataset.scrollOffset, 10) || 0;
      this.highlightCurrent = this.hasAttribute('data-highlight');
      this.updateUrl = this.hasAttribute('data-update-url');
      this.revealOnScroll = this.hasAttribute('data-reveal-on-scroll');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.toggle = this.querySelector('.toc__toggle');
      this.panel = this.querySelector('.toc__panel');
      this.list = this.querySelector('.toc__list');
      if (!this.toggle || !this.panel || !this.list) return;

      this.placeInFirstSection();
      this.build();

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:load', () => this.build());
      }
    }

    /**
     * The widget lives inside the first section within <main> so keyboard and
     * screen-reader users reach it early in the document order. It renders
     * after content_for_layout, so relocate it — except in the Theme Editor,
     * where section re-renders would wipe a relocated widget.
     */
    placeInFirstSection() {
      const firstSection = this.main.querySelector(':scope > .shopify-section');
      if (!firstSection) return;

      if (!(window.Shopify && window.Shopify.designMode) && !firstSection.contains(this)) {
        firstSection.appendChild(this);
      }

      if (!document.getElementById('hero')) {
        const anchor = document.createElement('span');
        anchor.id = 'hero';
        anchor.className = 'toc-hero-anchor';
        anchor.setAttribute('tabindex', '-1');
        firstSection.prepend(anchor);
      }
      this.heroAnchor = document.getElementById('hero');
    }

    build() {
      this.headings = this.collectHeadings();
      if (this.headings.length < this.minHeadings) {
        this.hidden = true;
        return;
      }

      this.assignIds();
      this.renderList();
      this.applyScrollMargins();
      this.bindEvents();
      this.hidden = false;
      if (this.revealOnScroll) this.updateReveal();
      if (this.highlightCurrent) this.highlight();
      this.scrollToHashTarget();
    }

    collectHeadings() {
      const selector = this.levels.join(',');
      return Array.from(this.main.querySelectorAll(selector)).filter((heading) => {
        if (heading.closest('table-of-contents, [data-toc-exclude], .visually-hidden')) return false;
        if (!heading.textContent.trim()) return false;
        if (!heading.getClientRects().length) return false; // display:none
        return true;
      });
    }

    slugify(text) {
      return text
        .trim()
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80)
        .replace(/-+$/, '');
    }

    assignIds() {
      const taken = new Set(Array.from(document.querySelectorAll('[id]'), (el) => el.id));
      this.headings.forEach((heading, index) => {
        if (heading.id && !this.isAutoId(heading.id)) return;
        const slug = this.slugify(heading.textContent) || `section-${index + 1}`;
        let candidate = slug;
        let suffix = 2;
        while (taken.has(candidate)) candidate = `${slug}-${suffix++}`;
        if (heading.id) {
          taken.delete(heading.id);
          this.retargetReferences(heading.id, candidate);
        }
        taken.add(candidate);
        heading.id = candidate;
      });
    }

    /* Theme-generated heading ids embed the section-instance marker
       ("template--<id>__<name>") and make ugly hashlinks — swap them for
       readable slugs. Author-set ids are kept so deep links stay stable. */
    isAutoId(id) {
      return id.includes('template--');
    }

    /* The tcl-* sections point aria-labelledby at their heading ids — follow
       any in-page references to the renamed heading. */
    retargetReferences(oldId, newId) {
      ['aria-labelledby', 'aria-describedby'].forEach((attr) => {
        document.querySelectorAll(`[${attr}~="${CSS.escape(oldId)}"]`).forEach((el) => {
          const tokens = el.getAttribute(attr).split(/\s+/);
          el.setAttribute(attr, tokens.map((t) => (t === oldId ? newId : t)).join(' '));
        });
      });
      document.querySelectorAll(`a[href="#${CSS.escape(oldId)}"]`).forEach((el) => {
        el.setAttribute('href', `#${newId}`);
      });
    }

    renderList() {
      this.list.innerHTML = '';
      this.links = this.headings.map((heading) => {
        const item = document.createElement('li');
        item.className = `toc__item toc__item--${heading.tagName.toLowerCase()}`;
        const link = document.createElement('a');
        link.className = 'toc__link';
        link.href = `#${heading.id}`;
        link.textContent = heading.textContent.trim();
        item.appendChild(link);
        this.list.appendChild(item);
        return link;
      });
    }

    headerOffset() {
      const header = document.querySelector('sticky-header, .section-header');
      const height = header ? header.getBoundingClientRect().height : 0;
      return Math.round(height) + this.extraOffset;
    }

    /* scroll-margin-top makes both native anchor jumps and scrollIntoView land
       clear of the sticky header. */
    applyScrollMargins() {
      const offset = `${this.headerOffset() + 8}px`;
      this.headings.forEach((heading) => (heading.style.scrollMarginTop = offset));
      if (this.heroAnchor) this.heroAnchor.style.scrollMarginTop = offset;
    }

    bindEvents() {
      if (this.bound) return;
      this.bound = true;

      this.toggle.addEventListener('click', () => this.setOpen(!this.open));

      this.panel.addEventListener('click', (event) => {
        const link = event.target.closest('a[href^="#"]');
        if (link) this.onLinkClick(event, link);
      });

      document.addEventListener('click', (event) => {
        if (this.open && !this.contains(event.target)) this.setOpen(false);
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.open) {
          this.setOpen(false);
          this.toggle.focus();
        }
      });

      window.addEventListener('scroll', () => this.onScroll(), { passive: true });

      window.addEventListener('resize', () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.applyScrollMargins(), 150);
      });
    }

    setOpen(open) {
      this.open = open;
      this.classList.toggle('toc--open', open);
      this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open && this.highlightCurrent) this.highlight();
    }

    onLinkClick(event, link) {
      const id = decodeURIComponent(link.getAttribute('href').slice(1));
      const target = document.getElementById(id);
      const behavior = this.reducedMotion.matches ? 'auto' : 'smooth';

      if (!target) {
        // "Back to top" without a #hero anchor (e.g. no section wrapper).
        if (id === 'hero') {
          event.preventDefault();
          this.setOpen(false);
          window.scrollTo({ top: 0, behavior });
        }
        return;
      }

      event.preventDefault();
      this.setOpen(false);
      target.scrollIntoView({ behavior, block: 'start' });

      // Keep keyboard/screen-reader reading position at the section jumped to.
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });

      // replaceState keeps the Back button pointing at the previous page
      // instead of walking back through every visited heading.
      if (this.updateUrl) history.replaceState(null, '', `#${id}`);
    }

    /* Heading ids are assigned client-side, so a shared #hash link cannot
       scroll natively on load — honour it once the ids exist. */
    scrollToHashTarget() {
      if (this.hashHandled || !window.location.hash) return;
      this.hashHandled = true;
      const target = document.getElementById(decodeURIComponent(window.location.hash.slice(1)));
      if (target && this.headings.includes(target)) {
        window.setTimeout(() => target.scrollIntoView({ behavior: 'auto', block: 'start' }), 0);
      }
    }

    onScroll() {
      if (this.scrollRaf) return;
      this.scrollRaf = requestAnimationFrame(() => {
        this.scrollRaf = null;
        if (this.revealOnScroll) this.updateReveal();
        if (this.highlightCurrent) this.highlight();
      });
    }

    updateReveal() {
      const revealed = window.scrollY > window.innerHeight * 0.5;
      this.classList.toggle('toc--revealed', revealed);
      if (!revealed && this.open) this.setOpen(false);
    }

    highlight() {
      if (!this.links || !this.links.length) return;
      const offset = this.headerOffset() + 24;
      let current = -1;
      this.headings.forEach((heading, index) => {
        if (heading.getBoundingClientRect().top <= offset) current = index;
      });
      this.links.forEach((link, index) => {
        link.classList.toggle('toc__link--current', index === current);
        if (index === current) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    }
  }

  customElements.define('table-of-contents', TableOfContents);
})();
